import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { PlayButtonRef } from 'components/atoms/play-button/PlayButton.data';
import type { VideoRef } from 'components/atoms/video/Video.data';
import type { ReactElement, RefObject, ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Quad } from 'gsap';
import { useScrollTransition } from 'util/react-transition-component';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import useEventListener from '../../../hooks/useEventListener';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import type { Video as VideoType } from '../../../util/storyblok/types/global.types';
import Heading from '../../atoms/heading/Heading';
import Image from '../../atoms/image/Image';
import PlayButton from '../../atoms/play-button/PlayButton';
import Text from '../../atoms/text/Text';
import Video from '../../atoms/video/Video';
import type { HeaderStandardProps } from './HeaderStandard.data';
import styles from './HeaderStandard.module.scss';
import { setupTransitionInTimeline, showText } from './HeaderStandard.transitions';
import { SiteHeaderContext } from '../../unlisted/SiteHeaderContext';
import type { SiteHeaderContextType } from '../../unlisted/SiteHeaderContext';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useGlobalStore } from '../../../stores/global';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import type { PrimaryButtonRef } from '../../atoms/primary-button/PrimaryButton.data';
import { NODE_HEADING, render } from 'storyblok-rich-text-react-renderer';
import type { HeadingSize } from '../../atoms/heading/Heading.data';

const removeOnPause = false;

export default function HeaderStandard({
  blok,
  canTransitionIn,
  ref,
  ...props
}: HeaderStandardProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const playButtonRef = useRef<PlayButtonRef>(null);
  const videoPlayerRef = useRef<VideoRef>(null);
  const mediaHolderRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [forcePlay, setForcePlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const buttonRef = useRef<PrimaryButtonRef>(null);

  const { inView } = useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        titleRef,
        subtitleRef,
        descriptionRef,
        playButtonRef,
        mediaHolderRef,
        buttonRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useEffect(() => {
    if (!inView) {
      setForcePlay(false);
    }
  }, [inView]);

  useThemeScrollBlock(elementRef, !!blok?.use_accent_color);

  const { siteHeaderElementRef } = useContext(SiteHeaderContext) as SiteHeaderContextType;
  useUpdateEffect(() => {
    showText({ titleRef, subtitleRef, descriptionRef, siteHeaderElementRef }, !forcePlay);
  }, [forcePlay]);

  const onPlayStateChange = useCallback((isPlaying: boolean) => {
    if (isPlaying || removeOnPause) {
      setForcePlay(isPlaying);
    }
    setIsPlaying(isPlaying);
  }, []);

  // @ts-ignore
  let video;
  if (blok?.video && blok.video.length > 0) {
    video = blok.video[0] as VideoType;
  }

  if (blok?.image?.filename && !video?.poster_image?.filename) {
    video = {
      ...video,
      poster_image: {
        ...blok?.image,
      },
    };
  }

  const isDesktopDevice = useIsDesktopDevice();
  const store = useGlobalStore();
  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;

  const scrollToTop = (callBack: () => void): void => {
    let currentY = scrollContext.scrollStatusRef.current?.offset.y;
    if (typeof currentY !== 'number') {
      currentY = 0;
    }
    const y = currentY + (elementRef.current?.getBoundingClientRect().top as number);

    if (y === currentY) {
      callBack();
    } else {
      scrollContext.scrollTo(0, y, 400, {
        easing: Quad.easeInOut,
        callback: () => {
          callBack();
        },
      });
    }
  };

  const onPlayButtonClick = useCallback(() => {
    if (!isDesktopDevice) {
      store.openVideoModal({
        // @ts-ignore
        video,
      });
    } else {
      scrollToTop(() => setForcePlay(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video, store, isDesktopDevice]);

  useScrollTrigger({
    trigger: elementRef,
    vars: {
      start: 'top bottom',
      end: 'bottom bottom-=5',
      onLeave() {
        setForcePlay(false);
      },
    },
  });

  const onKeydown = ({ key }: KeyboardEvent): void => {
    if (!isPlaying) return;
    if (key === 'Escape') {
      setForcePlay(false);
    }
  };

  useEventListener('keydown', onKeydown);

  const hasVideo = !!(video?.vimeo_url || video?.hosted_video?.filename);

  const mapHeading = (level: number): string => {
    if (level) {
      return `heading-0${level}`;
    }
    return `heading-02`;
  };

  return (
    <div
      ref={elementRef}
      className={classNames(styles.headerStandard, props.className, {
        [styles.noFullHeight]: blok.no_full_height,
      })}
      {...sbEditable(blok)}
    >
      {(blok?.image?.filename || hasVideo) && (
        <div ref={mediaHolderRef} className={classNames(styles.mediaHolder, 'abs-fill')}>
          {hasVideo && (
            <Video
              ref={videoPlayerRef}
              blok={video as VideoType}
              objectFit="contain"
              controls
              playing={forcePlay}
              playButton={false}
              muted={false}
              innerTransition={{
                type: 'scaleUp',
              }}
              removeOnPause={removeOnPause}
              onPlayStateChange={onPlayStateChange}
              className={classNames(styles.video, 'abs-fill')}
              isFullScreen
              exitFullScreen={() => {
                setForcePlay(false);
              }}
            />
          )}

          {blok?.image?.filename && !hasVideo && (
            <Image
              src={blok.image.filename}
              srcDesktop={blok.image.filename}
              className={classNames(styles.image, 'abs-fill')}
              layout="fill"
              objectFit="cover"
              innerTransition={{
                type: 'scaleUp',
              }}
              alt={blok.image.alt}
            />
          )}
        </div>
      )}

      <div
        className={classNames(styles.content, 'grid-gutter max-content-width')}
        style={{
          pointerEvents: forcePlay ? 'none' : 'all',
        }}
      >
        {typeof blok.title === 'string' && blok.title && (
          <Heading
            ref={titleRef}
            as="h1"
            size="heading-02"
            disableTriggerInOnScroll
            childClassName={classNames(styles.title)}
            enableChineseTransitionFallback
            parallax={{
              config: {
                speed: 3,
              },
              trigger: elementRef,
            }}
          >
            {blok.title}
          </Heading>
        )}

        {typeof blok.title !== 'string' &&
          blok.title?.content &&
          render(blok.title, {
            nodeResolvers: {
              [NODE_HEADING]: (children: ReactNode, { level }) => (
                <>
                  <Heading
                    ref={
                      /* @ts-ignore */
                      blok.title.content.length === 1
                        ? titleRef
                        : level === 1
                          ? titleRef
                          : subtitleRef
                    }
                    as="h1"
                    disableTriggerInOnScroll
                    enableChineseTransitionFallback
                    parallax={{
                      config: {
                        speed: 3,
                      },
                      trigger: elementRef,
                    }}
                    className={classNames(styles[mapHeading(level)], styles.title)}
                    size={mapHeading(level) as HeadingSize}
                    // @ts-ignore the following key is used when editing in Storyblok, otherwise title doesnt get updated
                    key={children?.[0] as string}
                  >
                    {children}
                    <br />
                  </Heading>
                </>
              ),
            },
          })}

        {blok.description && (
          <Text
            ref={descriptionRef}
            as="p"
            size="body-large"
            parallax={{
              config: {
                speed: 1.5,
              },
              trigger: elementRef,
            }}
            disableTriggerInOnScroll
            className={classNames(styles.description)}
          >
            {blok.description}
          </Text>
        )}

        {blok?.link && blok?.link.length > 0 && blok?.link[0] && (
          <PrimaryButton
            ref={buttonRef}
            className={classNames(styles.button)}
            link={{
              href: blok?.link[0]?.link?.cached_url,
              uuid: blok?.link[0]?.link?.id,
            }}
            label={blok?.link[0].title}
            disableTriggerInOnScroll
          />
        )}

        {hasVideo && (
          <PlayButton
            ref={playButtonRef}
            className={classNames(styles.playButton)}
            isPlaying={isPlaying}
            onClick={onPlayButtonClick}
            disableTriggerInOnScroll
            triggerInOnce
            isVisible={{
              onMount: false,
              value: !forcePlay,
            }}
          />
        )}
      </div>
    </div>
  );
}
